import { useState } from "react";
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { OrganizationCrewUb } from "../gql/graphql";
import { useDeleteCrewMutation } from "./useDeleteCrewMutation";
import { CyDeleteCrewDialog } from "./CyDeleteCrewDialog";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  crew?: OrganizationCrewUb;
  onDeleted?: () => void;
}

export function DeleteCrewDialog({ isOpen, toggle, crew, onDeleted }: Props) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [, deleteCrew] = useDeleteCrewMutation();

  const canDelete = !!crew && crew.playlistCount === 0;

  const handleDelete = async () => {
    if (!crew || !canDelete || isDeleting) return;

    setIsDeleting(true);
    const result = await deleteCrew({
      identifier: crew.identifier,
      bucket: crew.organizationId,
    });
    setIsDeleting(false);

    if (result.error) {
      console.error("Failed to delete crew", result.error);
      const backendMessage = result.error.graphQLErrors[0]?.message;
      window.alert(backendMessage ?? t("crew.manage.deleteError"));
      return;
    }

    onDeleted?.();
    toggle();
  };

  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (canDelete && !isDeleting) {
        handleDelete();
      }
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      toggle();
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  if (!crew) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {t("crew.manage.deleteDialog.title")}
      </ModalHeader>
      <ModalBody>
        {crew.playlistCount > 0 ? (
          <Alert color="danger" fade={false}>
            {t("crew.manage.deleteBlockedByPlaylists", {
              count: crew.playlistCount,
            })}
          </Alert>
        ) : (
          <Alert color="warning" fade={false}>
            <h4 className="alert-heading">
              {t("crew.manage.deleteDialog.confirmHeading")}
            </h4>
            <p>
              {t("crew.manage.deleteDialog.confirmMessage", {
                crewTitle: crew.title,
              })}
            </p>
            <hr />
            <p className="mb-0">
              {t("crew.manage.deleteDialog.cannotBeUndone")}
            </p>
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("common.buttons.cancel")}
        </Button>
        <Button
          color="danger"
          data-cy={CyDeleteCrewDialog.ConfirmButton}
          onClick={handleDelete}
          disabled={!canDelete || isDeleting}
        >
          {isDeleting ? t("crew.manage.deleting") : t("common.buttons.delete")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
